// @ts-nocheck
/* eslint-disable */
import { node } from "~/util/dom";
import { M3D_TEXTURE } from './texture-mapping';
import { debounce } from "~/lib/debounce";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import Melown3d from "~/melown3d";
import Melown3dTexture from "~/melown3d/texture";
import { MAP_ZOOM_CORRECTION } from "~/melown3d/pos";
import Melown3dFalloutCompass from "~/melown3d/fallout-compass";
import ScenePixel from "~/scene/common/pixel";
import SceneProvider from "~/scene/provider";
import Left3dVisualiser from "~/scene/provider/3d/visualiser";
import { ROUTE_CONST } from "~/route/const";
import { getPOI } from "~/route/utils";
import { store } from "~/state/store";
import { setCoordinates, setOphotoDate, setCopyrights } from "~/state/map/state";
import { getLocation } from "~/whereami";

export default class M3DProvider extends SceneProvider {
	constructor(scene) {
		super(scene, Left3dVisualiser);

		this._const = {
			VADSTENA_Z_FACTOR: 0.82,
			FOV: 45,
			MIN_ROTATE_ZOOM: 15,
			START_PITCH: -45,
			SUPPORTED_SIGNALS: [
				"map-redraw"
			],
			UNLOAD_TIMEOUT: 1000
		};

		this._rootEl = null;
		this._presenter = null;
		this._loadPromise = null;
		this._signalsList = [];
		this._lastPos = null;
		this._clickCb = null;
		this._contextMenu = false;
		this._3DMapset = "";
		this._binds = {
			removeCard: this._removeCard.bind(this)
		};
		this._isFalloutCompass = false;
		this._toRemove = [];
		this._isDeactivateInProgress = false;
		this._debounceSetCopyrights = debounce(copyrights => {
			if (!this._isDeactivateInProgress) {
				store.dispatch(setCopyrights(copyrights));
				store.dispatch(setOphotoDate(""));
			}
		}, 250);

		this._historyReplace = debounce(function() {
			Mapy.getComponent("history")?.save(true);
		}, 800);
	}

	/**
	 * Hlavni 3d presenter pro visualiser.
	 *
	 * @return {Melown3d}
	 */
	getPresenter() {
		return this._presenter;
	}

	/* *********************** interface *************************************** */

	/**
	 * Bind signalu nad providerem - nabindujeme si vystup signalu na handleSignal.
	 * Zatim je podpora pouze pro map-redraw.
	 *
	 * @param  {String} name Nazev signalu
	 */
	addListener(name) {
		if (this._const.SUPPORTED_SIGNALS.indexOf(name) == -1) return;

		this._signalsList.push(name);
	}

	/**
	 * Odstraneni poslouchani na nazev signalu - vola scena.
	 *
	 * @param  {String} name Nazev signalu
	 */
	removeListener(name) {
		let ind = this._signalsList.indexOf(name);

		if (ind != -1) {
			this._signalsList.splice(ind, 1);
		}
	}

	/**
	 * Obal pro zavolani eventu nad scenou. Pokud se jedna o nepodporovany signal, nic se nestane.
	 *
	 * @param  {String} name Nazev signalu
	 * @param  {Object} [data = null] Vlastni data
	 * @param  {SceneFeature} [sender] Kdo to volal?
	 */
	makeEvent(name, data, sender) {
		if (this._signalsList.indexOf(name) != -1) {
			this._scene.makeEvent(name, data, sender);
		}
	}

	/**
	 * Nastaveni callback funkce pro kliknuti do mapy. Pouziti na starem userwebu, vyhnout se ve windy!
	 *
	 * @param {Function} cb Callback funkce
	 */
	setMapClick(cb) {
		this._clickCb = cb;
	}

	/**
	 * Nastav kurzor provideru.
	 *
	 * @param {String} cursor Styl pro kurzor
	 */
	setCursor(cursor) {
		if (this._presenter) {
			this._presenter.getContainer().style.cursor = cursor || "";
		}
	}

	/**
	 * Ziskani aktualniho viewportu ve WGS84
	 *
	 * @return {Object} klice lb (left bottom), rt (right top) + osy x a y
	 */
	getViewport() {
		return (this._presenter ? this._presenter.getViewport() : {
			lbx: 0,
			lby: 0,
			rtx: 0,
			rty: 0
		});
	}

	/**
	 * Nastav stred projekce providera.
	 *
	 * @param {SceneCoords} center
	 */
	setCenter(center) {
		let wgs84 = center.toWGS84();

		this._presenter?.setCenter(wgs84[0], wgs84[1]);
	}

	/**
	 * Nastav stred a zoom projekce providera.
	 *
	 * @param {SceneCoords} center
	 * @param {Number} [zoom] Volitelny zoom
	 * @param {Boolean} [noAnim = false] Neanimovat?
	 * @return {Promise}
	 */
	setCenterZoom(center, zoom, noAnim) {
		let wgs84 = center.toWGS84();

		if (this._presenter) {
			return this._presenter.setCenterZoom(wgs84[0], wgs84[1], zoom);
		} else {
			return Promise.resolve();
		}
	}

	setPosition(pos) {
		this._presenter?.setPosition(pos);
	}

	/**
	 * Uzamknuti providera.
	 */
	lock() {
		this._presenter?.lock();
	}

	/**
	 * Odemknuti providera.
	 */
	unlock() {
		this._presenter?.unlock();
	}

	/**
	 * Prekresleni providera.
	 */
	redraw() {
		this._presenter?.redraw();
	}

	reload(state) {
		return new Promise(resolve => {
			this._deactivate().then(() => {
				setTimeout(() => {
					this._activate(state, {
						mapset: this._3DMapset
					}).then(() => {
						this._restoreFromState(state);
						resolve();
					}, () => {});
				}, this._const.UNLOAD_TIMEOUT + 100);
			}, () => {});
		});
	}

	addFreeLayer(name, lines, layerStyle = {}) {
		if (this._presenter) {
			this._presenter.addFreeLayer(name, lines, layerStyle);
		}
	}

	setMapset(mapset) {
		this._3DMapset = mapset;
		this._presenter?.setMapset(mapset);
	}

	/**
	 * Prevod pixelu na souradnice pomoci projekct providera.
	 *
	 * @param  {ScenePixel} pixel Vstupni pixel
	 * @param  {Number} [zoom] Vstupni zoom
	 * @return {SceneCoords}
	 */
	pixelToCoords(pixel, zoom) {
		if (this._presenter?.pixelToCoords(pixel.x, pixel.y)) {
			const coords = this._presenter.pixelToCoords(pixel.x, pixel.y);

			return this._scene.coordsFromWGS84(coords.x, coords.y);
		}

		const { longitude, latitude } = store.getState().map;

		return this._scene.coordsFromWGS84(longitude, latitude);
	}

	/**
	 * Prevod souradnic na pixel pomoci projekct providera.
	 *
	 * @param  {SceneCoords} pixel Vstupni souradnice
	 * @param  {Number} [zoom] Vstupni zoom
	 * @return {ScenePixel}
	 */
	coordsToPixel (coords, zoom) {
		let pixel = this._presenter?.coordsToPixel(coords.x, coords.y) || { x: 0, y: 0 };

		return new ScenePixel(pixel.x, pixel.y);
	}

	/**
	 * Vytvoreni obrazku se screenshotem 3d.
	 *
	 * @param {Boolean} [showLogo = true] zobrazit logo
	 * @return {Object} src, width, height
	 */
	getScreenshot (showLogo = true) {
		if (this._presenter) {
			return this._presenter.getScreenshot(showLogo);
		}
		else {
			return null;
		}
	}

	setInteraction(bool) {
		this._presenter?.ui.setInteraction(bool);
	}

	/**
	 * Aktivace provideru.
	 *
	 * @param  {Object} state Aktualni stav sceny
	 * @return {JAK.Promise}
	 */
	_activate() {
		let promise = new JAK.Promise();

		this._rootEl = document.getElementById("left3d");
		this._isDeactivateInProgress = false;

		this._load().then(() => {
			const { longitude, latitude, zoom, yaw, pitch, height, mapset } = store.getState().map;
			const pos = this._getPositionFromObj({
				x: longitude,
				y: latitude,
				z: zoom,
				...yaw ? { yaw } : {},
				...pitch ? { pitch } : {},
				...height ? { height } : {},
			});

			let opts = {
				map: {
					url: CONFIG.Left3d.url,
					onCopyright: credits => {
						this._debounceSetCopyrights(credits);
					},
				},
				pos,
				conf: {
					texture: new Melown3dTexture(M3D_TEXTURE.src, M3D_TEXTURE.mapping, M3D_TEXTURE.ratio),
					othersLang: _("m3d.andOthers"),
					inFrame: CONFIG.inFrame,
					scene: this._scene,
					noCopyright: true,
					mapserverUrl: CONFIG.mapserver.url,
					loaderImgCrossOrigin: CONFIG.mapserver.crossOrigin,
				},
				trans: {
					worldSides: _("m3d.worldSides"),
					infoTitle: _("m3d.info.title"),
					infoPan: _("m3d.info.pan"),
					infoZoom: _("m3d.info.zoom"),
					infoPitch: _("m3d.info.pitch"),
					copyrightLicence: _("copyright.licence"),
				},
			};

			if (this._isFalloutCompass) {
				opts.conf.compass = Melown3dFalloutCompass;
			}

			if (CONFIG.inFrame) {
				opts.conf.compass = null;
			}

			opts.conf.mapset = mapset;
			this._3DMapset = mapset;

			// fix pro seznam browser
			if (navigator && navigator.userAgent.indexOf("SznProhlizec") !== -1) {
				opts.map.asyncImageDecode = false;
			}

			this._presenter = new Melown3d(this._rootEl, opts);
			this._presenter.setUIEnabled(true);
			this._presenter.addListener("load", () => {
				promise.fulfill();
			});

			// start pozice
			let startPos = this._presenter.getPosition();

			this._presenter.addListener("map-load", () => {
				// pozice po nacteni
				let curPos = this._presenter.getPosition();

				// animace pouze pokud jsme se ve 3d nehybali
				if (yaw === 0 && pitch === 0 && curPos.x === startPos.x && curPos.y === startPos.y && curPos.z === startPos.z) {
					this._presenter.animateTo({
						pitch: this._const.START_PITCH
					}, {
						steps: 100
					});
				}
			});
			this._presenter.addListener("error", this._onError, this);
			this._presenter.addListener("autorotation-change", this._autorotationChange, this);
			this._presenter.addListener("position-change", this._positionChange, this);
			this._presenter.addListener("m3d-click", this._m3dClick, this);
			this._presenter.addListener("m3d-right-click", this._m3dRightClick, this);

			this._build();
		}, () => {});

		return promise;
	}

	/**
	 * Deaktivace provideru.
	 *
	 * @return {JAK.Promise}
	 */
	_deactivate() {
		let promise = new JAK.Promise();

		this._isDeactivateInProgress = true;

		if (this._presenter) {
			this._presenter.backToOphoto().then(pos => {
				// finalni parametry -> preneseme do store
				store.dispatch(setCoordinates({
					longitude: pos.x,
					latitude: pos.y,
					zoom: pos.mercZmap,
					yaw: 0,
					pitch: 0,
					height: 0,
					ignore: true,
				}));

				this._presenter.removeListener("error", this._onError, this);
				this._presenter.removeListener("autorotation-change", this._autorotationChange, this);
				this._presenter.removeListener("position-change", this._positionChange, this);
				this._presenter.removeListener("m3d-click", this._m3dClick, this);
				this._presenter.removeListener("m3d-right-click", this._m3dRightClick, this);

				this._signalsList = [];
				this._lastPos = null;

				let cont = this._presenter.getContainer();
				cont.classList.add("unload");

				// aktivace dalsiho providera
				promise.fulfill();

				setTimeout(() => {
					// vycisteni vytvorenych dat
					this._visualiser.clear();
					this._presenter && this._presenter.destroy();
					this._presenter = null;
					this._isDeactivateInProgress = false;

					while (this._toRemove.length) {
						this._toRemove.pop().remove();
					}

					cont.classList.remove("unload");
				}, this._const.UNLOAD_TIMEOUT);
			}, () => {});
		} else {
			promise.fulfill();
		}

		return promise;
	}

	/**
	 * Nacteni js dat - knihovna melownu a cele 3d v jenom souboru.
	 *
	 * @return {JAK.Promise}
	 */
	_load() {
		if (!this._loadPromise) {
			let files = ["vts-core.min.js"];

			this._loadPromise = Promise.all(files.map(file => Mapy.Util.loadScript("/js/" + file)));
		}

		return this._loadPromise;
	}

	/**
	 * Ze vstupnich parametru nachysta objekt s polohou pro 3d.
	 *
	 * @param  {Object} [params] Vstupni objekt s parametry
	 * @return {Object}
	 */
	_getPositionFromObj(params) {
		let posOpts = {
			fov: this._const.FOV
		};

		// stary + novy format
		for (let key in params) {
			let value = params[key];

			if (typeof value !== "number") {
				value = parseFloat(value);
			}

			switch (key) {
				// posx, posy se ignoruje - stred 3D je bud z [x,y] nebo [3dx, 3dy]
				case "x":
					posOpts.x = value;
					break;

				case "y":
					posOpts.y = value;
					break;

				case "posz":
					// fallback pro vadstenu
					posOpts.z = value * this._const.VADSTENA_Z_FACTOR;
					break;

				case "height":
					posOpts.z = value;
					break;

				case "z":
					posOpts.mercZ = value + MAP_ZOOM_CORRECTION;
					break;

				case "orientx": // fallback pro vadstenu
				case "yaw":
					posOpts.yaw = value;
					break;

				case "pitch":
				case "orienty":
					posOpts.pitch = value;
					break;
			}
		}

		// pouze pokud neni dostupna vyska spocitame vysku ze zoomu
		if ("mercZ" in posOpts && "z" in posOpts) {
			delete posOpts["mercZ"];
		}

		// stary format - utm, fallback pro vadstenu
		if ("posx" in params && "posy" in params) {
			let utmCoords = this._scene.coordsFromUTM33(parseFloat(params.posx), parseFloat(params.posy));

			posOpts.x = utmCoords.x;
			posOpts.y = utmCoords.y;
		}

		return posOpts;
	}

	/**
	 * Pri inicializaci 3d se vyskytla chyba.
	 */
	_onError(e) {
		this._rootEl.classList.add("error");

		var span = node("span", {className: "message"});

		if (e.message == "Shader error") {
			span.appendChild(JAK.ctext(_("m3d.shaderError")));
		}
		else if (vts.checkSupport()) {
			span.appendChild(JAK.ctext(_("m3d.error")));
		}
		else {
			this._rootEl.appendChild(Mapy.Util.createBigOldBrowserInfo(true));
		}

		this._rootEl.appendChild(span);
	}

	/**
	 * Zmena autorotace - prepis url.
	 */
	_autorotationChange() {
		Mapy.getComponent("history")?.save(true);
	}

	/**
	 * Zmena pozice ve 3d - chceme updatovat data sceny.
	 * Pokud se pohnul stred - prepiseme historii + signal.
	 *
	 * @param  {Object} pos Pozice ze 3d
	 */
	_positionChange(pos) {
		if (this._scene) {
			// jen yaw, pitch, height
			if (!this._lastPos || (this._lastPos.x == pos.x && this._lastPos.y == pos.y && this._lastPos.z == pos.z)) {
				store.dispatch(setCoordinates({
					yaw: pos.yaw,
					pitch: pos.pitch,
					height: pos.z,
					ignore: true,
				}));
			// update cele mapy
			} else {
				store.dispatch(setCoordinates({
					longitude: pos.x,
					latitude: pos.y,
					zoom: pos.mercZmap,
					yaw: pos.yaw,
					pitch: pos.pitch,
					height: pos.z,
					ignore: true,
				}));

				this.makeEvent("map-redraw");
			}
		}

		// update url
		this._historyReplace();

		this._lastPos = pos;
	}

	/**
	 * Vytvoreni UI prvku. Tlacitko zpatky do mapy + zoom.
	 */
	_build() {
	}

	/**
	 * Bylo kliknuto ve 3d. Pridava body do routovace.
	 *
	 * @param  {Object} pos Data s pozici.
	 */
	_m3dClick(pos) {
		if (this._clickCb) {
			this._clickCb.mapClick(pos.e);
		} else {
			let route = Mapy.getComponent("route");

			if (route.isActive() && this._scene) {
				route.m3dClick(pos.e, this._scene.coordsFromWGS84(pos.x, pos.y));
			}
		}
	}

	/**
	 * Bylo kliknuto ve 3d pravym cudlem mysi.
	 * Vyuzijeme css tridu u smapy - chceme mit planovat trasu na bod + whereami
	 *
	 * @param  {Object} pos Data s pozici.
	 */
	_m3dRightClick(pos) {
		// je otevreno - zavreme
		let route = Mapy.getComponent("route");
		let coords = this._scene.coordsFromWGS84(pos.x, pos.y);

		let el = document.createElement("div");
		el.classList.add("context-menu");
		el.style.borderTopLeftRadius = 0;
		el.style.left = pos.e.clientX + "px";
		el.style.top = pos.e.clientY + "px";

		document.addEventListener("mousedown", this._binds.removeCard);

		let contextRouteFrom = document.createElement("a");
		contextRouteFrom.classList.add("item");
		contextRouteFrom.href = "#";
		contextRouteFrom.innerHTML = _("route.contextRouteFrom");
		contextRouteFrom.addEventListener("click", e => {
			getPOI(coords).then(poi => {
				let items = Mapy.CML.state.route?.items || [];
				items.unshift(poi);
				if (!route.isActive()) {
					route.activate();
				}
				Mapy.CML.state = {
					route: {
						items
					}
				};
				Mapy.getComponent("history").save();
			}, () => {});
			this._removeCard();
		});

		let contextRouteTo = document.createElement("a");
		contextRouteTo.classList.add("item");
		contextRouteTo.href = "#";
		contextRouteTo.innerHTML = _("route.contextRouteTo");
		contextRouteTo.addEventListener("click", e => {
			getPOI(coords).then(poi => {
				let items = Mapy.CML.state.route?.items || [];
				poi.position = ROUTE_CONST.position.end;
				items.push(poi);
				if (!route.isActive()) {
					route.activate();
				}
				Mapy.CML.state = {
					route: {
						items
					}
				};
				Mapy.getComponent("history").save();
			}, () => {});
			this._removeCard();
		});

		let sep = document.createElement("span");
		sep.classList.add("item");
		sep.classList.add("separator");

		let whereAmI = document.createElement("a");
		whereAmI.classList.add("item");
		whereAmI.href = "#";
		whereAmI.innerHTML = _("whereAmI");
		whereAmI.addEventListener("click", e => {
			getLocation(coords).then(data => {
				Mapy.getComponent("detail").setState({
					source: data.source,
					id: data.id,
					coords,
					gpsCoords: data.gpsCoords,
					manual: true,
					show: false
				}).then(() => {
					Mapy.getComponent("history").save();
				}, () => {});
			});

			this._removeCard();
		});

		el.appendChild(contextRouteFrom);
		el.appendChild(contextRouteTo);
		el.appendChild(sep);
		el.appendChild(whereAmI);

		let cover = this._presenter.addCard(el, true);
		cover.addEventListener("mousedown", e => {
			if (e.target != cover) return;

			this._removeCard();
		});

		this._contextMenu = true;
	}

	/**
	 * Odstraneni karty - 3d kontext menu.
	 */
	_removeCard() {
		document.removeEventListener("mousedown", this._binds.removeCard);

		this._contextMenu = false;
		this._presenter?.removeCard(true);
	}
}
